import { AgGridAngular } from '@ag-grid-community/angular';
import { GridApi, GridReadyEvent } from '@ag-grid-community/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  model,
  Signal,
  signal,
  untracked,
} from '@angular/core';
import dayjs from 'dayjs';
import isNumber from 'lodash-es/isNumber';

import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';

import { injectInvestigatorForecastPeriodData } from '@models/forecast/api/investigator-forecast.api';
import {
  injectTrialSettingByName,
  injectUpdateTrialSettingMutation,
} from '@models/trial-settings/api/trial-settings.api';

import { TablePanelComponent } from '@shared/components/table-panel/table-panel.components';
import { TableSkeletonComponent } from '@shared/components/table-skeleton/table-skeleton.component';
import {
  Currency,
  listInvestigatorForecastPeriodDataQuery,
  TrialSettingKey,
} from '@shared/services/gql.service';
import { injectFlags } from '@shared/services/launch-darkly.service';
import { MainQuery } from '@shared/store/main/main.query';
import { decimalDivide } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { PATIENT_INVOICEABLES_BUFFER_COL_ID } from './constants/patient-invoiceables-buffer-col-id.const';
import { CostType } from './enums/cost-type';
import { injectExcelParams } from './grid-config/investigator-forecast-excel-config';
import { investigatorForecastTableConfig } from './grid-config/investigator-forecast-table-config';
import { InvestigatorForecastGridRow, Period } from './types/investigator-forecast-grid-row';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AgGridAngular, TablePanelComponent, TableSkeletonComponent, ExportExcelButtonComponent],
  selector: 'aux-investigator-forecast-table',
  templateUrl: './investigator-forecast-table.component.html',
})
export class InvestigatorForecastTableComponent {
  mainQuery = inject(MainQuery);

  periods: Signal<Period[]> = computed(() => {
    if (!this.mainQuery.trialStartDate() || !this.mainQuery.trialEndDate()) {
      return [];
    }

    const periods: Period[] = [];

    const currentYear = dayjs(this.mainQuery.currentOpenMonth()).year();
    const startDate = dayjs(this.mainQuery.trialStartDate());
    const endDate = dayjs(this.mainQuery.trialEndDate());

    const pastYears = new Set<number>();

    let currentDate = startDate.clone();

    const LAST_MONTH_IN_YEAR = 11;

    while (currentDate.isSameOrBefore(endDate, 'month')) {
      const dateYear = currentDate.year();

      // For years before the current year, add a single column for the entire year
      if (dateYear < currentYear) {
        if (!pastYears.has(dateYear)) {
          pastYears.add(dateYear);
          periods.push(dateYear.toString() as Period);
        }

        // Move to the first month of the next year
        if (currentDate.month() === LAST_MONTH_IN_YEAR) {
          currentDate = currentDate.add(1, 'month');
        } else {
          currentDate = dayjs(`${dateYear + 1}-01-01`);
        }
      } else {
        // For current year and future, add monthly columns
        periods.push(currentDate.format('YYYY-MM-01') as Period);
        currentDate = currentDate.add(1, 'month');
      }
    }

    return periods;
  });

  patientInvoiceablesPercentage = model<number | undefined>();

  gridOptions = computed(() =>
    investigatorForecastTableConfig({
      currency: Currency.USD,
      currentOpenMonth: this.mainQuery.currentOpenMonth(),
      patientInvoiceablesPercentage: this.patientInvoiceablesPercentage,
      periods: this.periods(),
    })
  );
  columnDefsCount = computed(() => this.gridOptions()?.columnDefs?.length ?? 0);

  excelOptions = injectExcelParams(this.columnDefsCount);
  flags = injectFlags({
    investigatorDiscontinuationRate: 'investigator_discontinuation_rate',
  });
  patientInvoiceablesPercentageSetting = injectTrialSettingByName(
    TrialSettingKey.TRIAL_SETTING_PATIENT_INVOICEABLE_PERCENTAGE
  );
  periodDataQuery = injectInvestigatorForecastPeriodData({
    enabled: computed(
      () =>
        !this.patientInvoiceablesPercentageSetting.isLoading() &&
        isNumber(this.patientInvoiceablesPercentage())
    ),
    input: computed(() => {
      return {
        end_date: this.mainQuery.trialEndDate() ?? '',
        patient_invoiceable_percentage: decimalDivide(
          this.patientInvoiceablesPercentage() ?? 0,
          100
        ),
        start_date: this.mainQuery.trialStartDate() ?? '',
      };
    }),
  });
  updateSiteSettingMutation = injectUpdateTrialSettingMutation();

  gridAPI = signal<GridApi<InvestigatorForecastGridRow> | null>(null);

  gridData: Signal<InvestigatorForecastGridRow[]> = computed(() => {
    return this.getGridData(this.periodDataQuery.data() ?? []);
  });
  isEmptyData = computed(() => !this.gridData().length);

  private readonly defaultPatientInvoiceablesPercentage = 10;

  _asyncPatientInvoiceablesPercentage = effect(() => {
    if (this.patientInvoiceablesPercentageSetting.isLoading()) return;

    const settingValue = this.patientInvoiceablesPercentageSetting.setting()?.setting_value;

    this.patientInvoiceablesPercentage.set(
      settingValue ? +settingValue : this.defaultPatientInvoiceablesPercentage
    );
  });
  _pinnedBottomRow = effect(() => {
    this.gridData();

    // Need to wait for ag-grid aggregation to complete
    const SET_TOTAL_ROW_DELAY = 100;

    setTimeout(() => {
      this.pinnedTotalRow();
      this.gridAPI()?.sizeColumnsToFit();
    }, SET_TOTAL_ROW_DELAY);
  });
  _syncGridOptions = effect(() => {
    // any changes to the grid options will trigger a re-render
    const newGridOptions = this.gridOptions();

    // don't track the gridAPI
    const gridAPI = untracked(() => this.gridAPI());

    if (!gridAPI) {
      return;
    }

    Object.entries(newGridOptions).forEach(([key, value]) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      gridAPI.setGridOption(key as any, value);
    });
  });
  _updatePatientInvoiceablesSetting = effect(() => {
    if (this.patientInvoiceablesPercentageSetting.isLoading()) return;

    const isEqual =
      this.patientInvoiceablesPercentageSetting.setting()?.setting_value ===
      this.patientInvoiceablesPercentage()?.toString();

    if (!isEqual) {
      this.updateSiteSettingMutation.mutate({
        setting_key: TrialSettingKey.TRIAL_SETTING_PATIENT_INVOICEABLE_PERCENTAGE,
        setting_value: this.patientInvoiceablesPercentage()?.toString() ?? '',
      });
    }
  });

  appendHiddenColumnsToExport = (visibility: boolean) => () => {
    this.gridAPI()?.setColumnVisible(PATIENT_INVOICEABLES_BUFFER_COL_ID, visibility);
  };

  private getCurrentPeriodInfo() {
    const currentPeriod = dayjs(this.mainQuery.currentOpenMonth()).format('YYYY-MM-01');
    const currentYear = dayjs(this.mainQuery.currentOpenMonth()).year();

    return { currentPeriod, currentYear };
  }

  private getGridData(
    data: listInvestigatorForecastPeriodDataQuery[]
  ): InvestigatorForecastGridRow[] {
    if (!data.length || !this.hasAnyCost(data)) {
      return [];
    }

    const { currentPeriod, currentYear } = this.getCurrentPeriodInfo();
    const rows = this.initializeRows();

    data.forEach((periodData) => {
      const { isBeforeCurrentMonth, isBeforeOrSameCurrentMonth, periodKey } = this.getPeriodInfo(
        periodData.period,
        currentYear
      );

      this.processPatientInvoiceables({
        currentPeriod,
        isBeforeCurrentMonth,
        patientInvoiceableActualAmount: periodData.patient_invoiceable_actual_amount,
        patientInvoiceableForecastAmount: periodData.patient_invoiceable_forecast_amount,
        patientInvoiceablesRows: rows.patientInvoiceablesRows,
        periodKey,
      });

      this.processPatientVisits({
        currentPeriod,
        isBeforeCurrentMonth,
        isBeforeOrSameCurrentMonth,
        patientVisits: periodData.patient_visits,
        patientVisitsRows: rows.patientVisitsRows,
        periodKey,
      });

      this.processSiteInvoiceables(
        rows.siteInvoiceablesRows,
        periodData,
        periodKey,
        isBeforeCurrentMonth
      );
    });

    rows.siteInvoiceablesRows.totalAmount = this.getTotalAmount(rows.siteInvoiceablesRows);
    rows.patientInvoiceablesRows.totalAmount = this.getTotalAmount(rows.patientInvoiceablesRows);

    return [
      ...(rows.patientVisitsRows.length ? rows.patientVisitsRows : [rows.emptyPatientVisitsRow]),
      rows.patientInvoiceablesRows,
      rows.siteInvoiceablesRows,
    ];
  }

  private getPeriodInfo(period: string, currentYear: number) {
    const periodDate = dayjs(period);
    const isBeforeCurrentMonth = periodDate.isBefore(this.mainQuery.currentOpenMonth(), 'month');
    const isBeforeOrSameCurrentMonth = periodDate.isSameOrBefore(
      this.mainQuery.currentOpenMonth(),
      'month'
    );
    const year = periodDate.year();
    const periodKey = currentYear > year ? year : period;

    return { isBeforeCurrentMonth, isBeforeOrSameCurrentMonth, periodKey };
  }

  getTotalAmount(row: InvestigatorForecastGridRow) {
    const excludeKeys = new Set<keyof InvestigatorForecastGridRow>([
      'costType',
      'id',
      'patientGroup',
      'totalAmount',
    ]);

    return Object.entries(row)
      .filter(([key]) => !excludeKeys.has(key as keyof InvestigatorForecastGridRow))
      .reduce((acc, [, value]) => acc + value, 0);
  }

  private hasAnyCost(data: listInvestigatorForecastPeriodDataQuery[]) {
    return data.some(
      ({
        patient_invoiceable_actual_amount,
        patient_invoiceable_forecast_amount,
        patient_visits,
        site_invoiceables,
      }) =>
        patient_invoiceable_actual_amount !== 0 ||
        patient_invoiceable_forecast_amount !== 0 ||
        !!patient_visits.length ||
        !!site_invoiceables.length
    );
  }

  private initializeRows() {
    return {
      emptyPatientVisitsRow: {
        costType: CostType.PATIENT_VISITS,
        id: Utils.uuid(),
        totalAmount: 0,
      } as InvestigatorForecastGridRow,
      patientInvoiceablesRows: {
        costType: CostType.PATIENT_INVOICEABLES,
        id: Utils.uuid(),
        totalAmount: 0,
      } as InvestigatorForecastGridRow,
      patientVisitsRows: [] as InvestigatorForecastGridRow[],
      siteInvoiceablesRows: {
        costType: CostType.SITE_INVOICEABLES,
        id: Utils.uuid(),
        totalAmount: 0,
      } as InvestigatorForecastGridRow,
    };
  }

  onGridReady(params: GridReadyEvent<InvestigatorForecastGridRow>) {
    this.gridAPI.set(params.api);
  }

  pinnedTotalRow() {
    const totals: Record<Period, number> = {};

    if (!this.gridData().length) {
      return;
    }

    this.gridAPI()?.forEachNode((node) => {
      if (node.aggData && node.level === 0) {
        Object.entries(node.aggData).forEach(([key, value]) => {
          totals[key as Period] = (totals[key as Period] || 0) + ((value as number) || 0);
        });
      }
    });

    this.gridAPI()?.setGridOption('pinnedBottomRowData', [
      {
        ...totals,
        costType: 'Total',
        id: Utils.uuid(),
        patientGroup: '',
      },
    ]);
  }

  private processPatientInvoiceables({
    currentPeriod,
    isBeforeCurrentMonth,
    patientInvoiceableActualAmount,
    patientInvoiceableForecastAmount,
    patientInvoiceablesRows,
    periodKey,
  }: {
    currentPeriod: string;
    isBeforeCurrentMonth: boolean;
    patientInvoiceableActualAmount: listInvestigatorForecastPeriodDataQuery['patient_invoiceable_actual_amount'];
    patientInvoiceableForecastAmount: listInvestigatorForecastPeriodDataQuery['patient_invoiceable_forecast_amount'];
    patientInvoiceablesRows: InvestigatorForecastGridRow;
    periodKey: number | string;
  }) {
    if (!isBeforeCurrentMonth) {
      patientInvoiceablesRows[periodKey as Period] =
        (patientInvoiceablesRows[periodKey as Period] || 0) +
        patientInvoiceableForecastAmount +
        patientInvoiceableActualAmount;
    } else {
      patientInvoiceablesRows[currentPeriod as Period] =
        (patientInvoiceablesRows[currentPeriod as Period] || 0) + patientInvoiceableForecastAmount;

      patientInvoiceablesRows[periodKey as Period] =
        (patientInvoiceablesRows[periodKey as Period] || 0) + patientInvoiceableActualAmount;
    }

    if (!patientInvoiceableActualAmount && !patientInvoiceableForecastAmount) {
      return;
    }
  }

  private processPatientVisits({
    currentPeriod,
    isBeforeCurrentMonth,
    isBeforeOrSameCurrentMonth,
    patientVisits,
    patientVisitsRows,
    periodKey,
  }: {
    currentPeriod: string;
    isBeforeCurrentMonth: boolean;
    isBeforeOrSameCurrentMonth: boolean;
    patientVisits: listInvestigatorForecastPeriodDataQuery['patient_visits'];
    patientVisitsRows: InvestigatorForecastGridRow[];
    periodKey: number | string;
  }) {
    patientVisits.forEach((visit) => {
      const amountKey = this.flags().investigatorDiscontinuationRate ? 'expected_amount' : 'amount';

      const periodAmount = isBeforeCurrentMonth
        ? visit.completed
          ? visit[amountKey]
          : 0
        : visit[amountKey];

      const isIncompleteVisitInCurrentOrPastMonth = isBeforeOrSameCurrentMonth && !visit.completed;

      const currentPeriodAmount = isIncompleteVisitInCurrentOrPastMonth ? visit[amountKey] : 0;

      const totalAmount = isIncompleteVisitInCurrentOrPastMonth
        ? currentPeriodAmount
        : periodAmount;

      if (!periodAmount && !currentPeriodAmount) {
        return;
      }

      patientVisitsRows.push({
        costType: CostType.PATIENT_VISITS,
        [currentPeriod as Period]: currentPeriodAmount,
        id: Utils.uuid(),
        patientGroup: visit.patient_group_name ?? 'Visit Cost',
        [periodKey as Period]: periodAmount,
        totalAmount,
      } as InvestigatorForecastGridRow);
    });
  }

  private processSiteInvoiceables(
    siteInvoiceablesRows: InvestigatorForecastGridRow,
    periodData: listInvestigatorForecastPeriodDataQuery,
    periodKey: number | string,
    isBeforeCurrentMonth: boolean
  ) {
    const { period, site_invoiceables } = periodData;

    if (isBeforeCurrentMonth) {
      const completedSiteInvoiceables =
        site_invoiceables?.reduce((acc, curr) => acc + curr.amount, 0) || 0;

      siteInvoiceablesRows[periodKey as Period] =
        (siteInvoiceablesRows[periodKey as Period] || 0) + completedSiteInvoiceables;
    } else {
      siteInvoiceablesRows[period as Period] =
        site_invoiceables?.reduce((acc, curr) => acc + curr.amount, 0) || 0;
    }
  }
}
